import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  ListItem,
  ListItemText,
  Checkbox
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import { getFormattedDate } from "../../../utils/commonFunctions";
import CustomButton from "../../../components/CustomButton/Button";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

interface UserTag {
  UserTagAllocationId: string;
  TagName: string;
  UserId: string;
  ActiveFrom: string;
  ActiveTo?: string;
}

interface TagSearchResult {
  TagName: string;
  Description?: string;
}

function UserTagsPage() {
  const { userId } = useParams();
  const [userTags, setUserTags] = useState<UserTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addTagLoading, setAddTagLoading] = useState(false);
  const [openAddTagModal, setOpenAddTagModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<TagSearchResult[]>([]);
  const [selectedTag, setSelectedTag] = useState<TagSearchResult | null>(null);
  const [notification, setNotification] = useState<{
    type: "success" | "error" | null;
    message: string | null;
  }>({
    type: null,
    message: null
  });
  const [searchAttempted, setSearchAttempted] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserTags();
    }
  }, [userId]);

  const fetchUserTags = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiBaseUrl}/tagging/usertagallocations/user/${userId}/active`,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setUserTags(response.data || []);
    } catch (err) {
      console.error("Failed to fetch user tags:", err);
      setNotification({
        type: "error",
        message: "Failed to fetch user tags"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchTags = async () => {
    setSearchAttempted(true);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await axios.get(
        `${apiBaseUrl}/tagging/usertag/${searchQuery}`,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      // Always set as array
      const data = response.data;
      setSearchResults(Array.isArray(data) ? data : data ? [data] : []);
    } catch (err) {
      console.error("Failed to search tags:", err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddTag = async () => {
    if (!selectedTag || !userId) return;

    try {
      setAddTagLoading(true);
      const tagData = {
        TagName: selectedTag.TagName,
        UserTagAllocationId: "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          }
        ),
        UserId: userId,
        ActiveFrom: new Date().toISOString().slice(0, 19).replace("T", " ")
      };

      await axios.post(`${apiBaseUrl}/tagging/usertagallocation`, tagData, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      });

      setOpenAddTagModal(false);
      setSearchQuery("");
      setSearchResults([]);
      setSelectedTag(null);
      setNotification({
        type: "success",
        message: "Tag allocated successfully"
      });
      await fetchUserTags();
    } catch (err) {
      console.error("Failed to allocate tag:", err);
      setNotification({
        type: "error",
        message: "Failed to allocate tag"
      });
    } finally {
      setAddTagLoading(false);
    }
  };

  const handleTagSelect = (tag: TagSearchResult) => {
    setSelectedTag(tag);
  };

  const columns = [
    {
      id: "TagName",
      label: "Tag Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "ActiveFrom",
      label: "Active From",
      minWidth: 150,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "ActiveTo",
      label: "Active To",
      minWidth: 150,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "Active")
    }
  ];

  return (
    <Box>
      {notification.message && (
        <Alert
          severity={notification.type || "info"}
          sx={{ mb: 2 }}
          onClose={() => setNotification({ type: null, message: null })}
        >
          {notification.message}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <CustomButton
          callBack={() => {
            setSelectedTag(null);
            setSearchQuery("");
            setSearchResults([]);
            setOpenAddTagModal(true);
          }}
        >
          Add Tag
        </CustomButton>
      </Box>

      <CustomTableWithPagination
        columns={columns}
        data={userTags}
        apiStatus={loading ? "fetching" : "idle"}
        payloadSize={100}
        currentPage={1}
        totalCount={userTags.length}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No tags found for this user"
        handleRowClick={() => {}}
      />

      {/* Add Tag Modal */}
      <Dialog
        open={openAddTagModal}
        onClose={() => !addTagLoading && setOpenAddTagModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Add Tag
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => !addTagLoading && setOpenAddTagModal(false)}
              disabled={addTagLoading}
              sx={{
                color: (theme) =>
                  addTagLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="subtitle2" mb={1}>
              Search for a tag to allocate:
            </Typography>
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                placeholder="Enter tag name to search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchAttempted(false);
                }}
                onKeyPress={(e) => e.key === "Enter" && handleSearchTags()}
                disabled={addTagLoading}
              />
              <CustomButton
                callBack={handleSearchTags}
                disabled={addTagLoading || !searchQuery.trim()}
                styles={{ marginTop: "0px !important" }}
              >
                {searchLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon />
                )}
              </CustomButton>
            </Box>
          </Box>

          {searchAttempted &&
            !searchLoading &&
            searchQuery.trim() &&
            searchResults.length === 0 && (
              <Box mb={2}>
                <Alert severity="info">
                  No tags found matching your search.
                </Alert>
              </Box>
            )}

          {searchResults.map((tag, index) => (
            <ListItem
              key={index}
              sx={{ px: 1 }}
              onClick={() =>
                selectedTag?.TagName === tag.TagName
                  ? setSelectedTag(null)
                  : handleTagSelect(tag)
              }
              dense
              button
            >
              <Checkbox
                edge="start"
                checked={selectedTag?.TagName === tag.TagName}
                tabIndex={-1}
                disableRipple
                sx={{
                  color: "#192F67",
                  "&.Mui-checked": {
                    color: "#192F67"
                  }
                }}
              />
              <ListItemText
                primary={tag.TagName}
                secondary={tag.Description}
                sx={{ ml: 1 }}
              />
            </ListItem>
          ))}
        </DialogContent>
        <DialogActions>
          <CustomButton
            callBack={() => !addTagLoading && setOpenAddTagModal(false)}
            disabled={addTagLoading}
            styles={{ minHeight: "36px" }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            callBack={handleAddTag}
            disabled={addTagLoading || !selectedTag}
            styles={{ minHeight: "36px" }}
          >
            {addTagLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Allocate Tag"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserTagsPage;
